import React, { useState, useEffect } from "react";
import "./styles.css";
import { Button, makeStyles, Spinner } from "@fluentui/react-components";
import { SplitVerticalRegular } from "@fluentui/react-icons";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import apiServices from "../../service";
import { setHeaderValue } from "../../redux/headerValue";
import { updateTableDataList } from "../../redux/tableDataSlice";
import { setTableData } from "../../redux/clientSlice";
import { setDynamicResponseData } from "../../redux/dynamicResponse";
import { ResponseItem, TableNameCounts } from "./DataSelection";
import ASCButton from "../../components/Button";

interface RecordToCopy {
  tableName: string;
  recordId: string;
}

interface SplitPayload {
  reason?: string;
  createClient: boolean;
  createPartner: boolean;
  partnerId: string;
  clientRecordsToCopy: RecordToCopy[];
  partnerRecordsToCopy: RecordToCopy[];
}

type SplitStatus = "idle" | "busy" | "success" | "error";

const useStyles = makeStyles({
  innerWrapper: {
    columnGap: "15px",
    display: "flex",
    justifyContent: "flex-end",
  },
});

const FinalSection = ({
  checkboxState,
  selectedReason,
  BackToDataSelection,
  cancelSplitData,
  onClose,
  setSelectedValues,
  setPartnerData,
  tableNameCounts,
  setTableNameCounts,
  setClientData,
  setCheckboxState,
  setSelectedReason,
}: {
  checkboxState: { client: boolean; partner: boolean };
  selectedReason?: string;
  BackToDataSelection: () => void;
  cancelSplitData: () => void;
  onClose: () => void;
  setSelectedValues: React.Dispatch<React.SetStateAction<string[]>>;
  setPartnerData: React.Dispatch<React.SetStateAction<ResponseItem[]>>;
  tableNameCounts: TableNameCounts;
  setTableNameCounts: React.Dispatch<React.SetStateAction<TableNameCounts>>;
  setClientData: React.Dispatch<React.SetStateAction<ResponseItem[]>>;
  setCheckboxState: React.Dispatch<
    React.SetStateAction<{ client: boolean; partner: boolean }>
  >;
  setSelectedReason: React.Dispatch<React.SetStateAction<string | undefined>>;
}) => {
  const dispatch: AppDispatch = useDispatch();
  const styles = useStyles();
  const [status, setStatus] = useState<SplitStatus>("idle");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const dynamicResponseData = useSelector(
    (state: any) => state.splitData.dynamicResponseData
  );
  const clientValue = useSelector(
    (state: RootState) => state.clientid.storedValue
  );
  const partnerId = useSelector((state: any) => state.partnerId.storedValue);
  useEffect(() => {
    const values: string[] = [];
    if (checkboxState.client) values.push(clientValue);
    if (checkboxState.partner) values.push(partnerId);
    setSelectedValues(values);
  }, [checkboxState, clientValue, partnerId]);

  const recordsAssignedTo = (filterKey: "Client" | "Partner"): RecordToCopy[] => {
    const records: RecordToCopy[] = [];
    Object.keys(dynamicResponseData).forEach((key) => {
      dynamicResponseData[key].forEach((record: any) => {
        if (record.assignedTo === filterKey) {
          records.push({
            tableName: record.tableName,
            recordId: record.recordId,
          });
        }
      });
    });
    return records;
  };

  const resetWizardState = () => {
    dispatch(setHeaderValue(""));
    dispatch(
      updateTableDataList({
        tableName: "",
        data: [],
      })
    );
    dispatch(setTableData([]));
    dispatch(
      setDynamicResponseData({
        asset: [],
        liability: [],
        policy: [],
        income: [],
        expense: [],
        dependant: [],
        address: [],
        note: [],
      })
    );
    setClientData([]);
    setPartnerData([]);
    setTableNameCounts({
      Client: {},
      Partner: {},
    });
    setCheckboxState({ client: false, partner: false });
    setSelectedReason("");
  };

  const closeAfterSuccess = () => {
    resetWizardState();
    onClose();
  };

  const splitData = () => {
    setStatus("busy");
    setErrorMessage("");

    const payload: SplitPayload = {
      reason: selectedReason,
      createClient: checkboxState.client,
      createPartner: checkboxState.partner,
      partnerId,
      clientRecordsToCopy: recordsAssignedTo("Client"),
      partnerRecordsToCopy: recordsAssignedTo("Partner"),
    };

    // One atomic call covering both sides -- either both new records are
    // created together, or neither is, instead of two independent requests
    // that could leave a half-completed split if the second one failed.
    apiServices.ClientPartnerSplit.splitCP(clientValue, payload)
      .then((response: any) => {
        // A caught backend error still comes back as HTTP 200 with
        // isError: true in the body (same convention as elsewhere in this
        // app, e.g. redux/Client/contacts.ts) rather than a rejected
        // promise -- check it explicitly instead of treating any resolved
        // response as a success.
        if (response?.data?.isError) {
          throw new Error(response.data.message || "Split failed. Please try again.");
        }
        setStatus("success");
      })
      .catch((error: any) => {
        setStatus("error");
        setErrorMessage(
          error?.response?.data?.message ||
            error?.message ||
            "Split failed. Please try again."
        );
      });
  };

  const navigationDisabled = status === "busy";

  return (
    <div>
      <div className="split-btn-section">
        <div>
          <span className="title-sections"> Make the Changes</span>
        </div>
        <div>
          <span>
            Click the split button to implement the changes described on the
            previous pages.
          </span>
        </div>
      </div>

      {status === "busy" && (
        <div className="spinner-container">
          <Spinner size="medium" label="Splitting client and partner records… please don't close this window." />
        </div>
      )}

      {status === "success" && (
        <div className="split-result">
          <p>Split completed successfully.</p>
          <ASCButton
            shape="rounded"
            appearance="primary"
            className="asc-button-primary"
            onClick={closeAfterSuccess}
            label="Close"
          />
        </div>
      )}

      {status === "error" && (
        <div className="split-result">
          <p role="alert">{errorMessage}</p>
          <ASCButton
            shape="rounded"
            appearance="secondary"
            icon={<SplitVerticalRegular />}
            onClick={splitData}
            label="Try Again"
          />
        </div>
      )}

      {status === "idle" && (
        <div className="box">
          <div className="btn-section">
            <ASCButton
              shape="rounded"
              appearance="secondary"
              icon={<SplitVerticalRegular />}
              onClick={splitData}
              label="Split"
            />
          </div>
        </div>
      )}

      {status !== "success" && (
        <div className={styles.innerWrapper}>
          <ASCButton
            shape="rounded"
            size="medium"
            appearance="primary"
            className="asc-button-primary"
            onClick={BackToDataSelection}
            disabled={navigationDisabled}
            label="Back"
          />
          <ASCButton
            shape="rounded"
            size="medium"
            onClick={cancelSplitData}
            disabled={navigationDisabled}
            label="Cancel"
          />
        </div>
      )}
    </div>
  );
};

export default FinalSection;
